import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";
import validator from "@rjsf/validator-ajv8";
import { Button, RjsfForm as Form, Spinner, Tabs, Terminal, Typography } from "@coolremote/shared";
import { useStoreActions } from "../../../models/RootStore";
import styles from "./DeviceConfiguration.module.css";
//todo : replace the linear progress from material ui with the one from shared components when it's implemented
import { LinearProgress } from "@material-ui/core";

interface ITerminalConfig {
  promptColor: string;
  style: any;
  commands: any;
  commandPassThrough: (cmd: string, print: (msg: string) => void) => void;
}

interface IDeviceConfigurationProps {
  deviceId: string;
  isConnected: boolean;
  terminalConfig: ITerminalConfig;
  canUpdateDeviceConfig?: boolean;
  onRetryConnection?: () => Promise<any>;
  scrollToField?: string;
}

const TERMINAL_TAB = "Terminal";

const INTERFACES_CATEGORY_KEY = "lines";

const DeviceConfiguration: React.FC<IDeviceConfigurationProps> = ({ deviceId, isConnected, terminalConfig, canUpdateDeviceConfig = false, onRetryConnection, scrollToField }) => {
  const { getDeviceConfigCategories, getDeviceConfigCategory, setDeviceConfigCategory } = useStoreActions((actions) => actions.devices);
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);

  const [selectedTab, setSelectedTab] = useState("");
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [formSchema, setFormSchema] = useState<any>({});
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingSchema, setIsLoadingSchema] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isTerminalBusy, setIsTerminalBusy] = useState(false);
  const schemaRequestIdRef = useRef(0);

  const fetchCategories = useCallback(async () => {
    if (!deviceId || !isConnected) {
      return;
    }
    setIsLoadingCategories(true);
    try {
      const response = await getDeviceConfigCategories({ deviceId });
      setCategories(response);
      const defaultTab = INTERFACES_CATEGORY_KEY in response ? INTERFACES_CATEGORY_KEY : Object.keys(response)[0] || "";
      setSelectedTab(defaultTab);
    } catch (error: any) {
      addMessage({ message: error.message });
    } finally {
      setIsLoadingCategories(false);
    }
  }, [deviceId, isConnected, getDeviceConfigCategories]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }
    fetchCategories();
  }, [fetchCategories, isConnected]);

  const handleRetry = async () => {
    if (isRetrying) {
      return;
    }
    setIsRetrying(true);
    try {
      await onRetryConnection?.();
    } finally {
      setIsRetrying(false);
    }
  };

  const fetchSchema = useCallback(async (silent = false) => {
    if (selectedTab === TERMINAL_TAB) {
      return;
    }

    if (!deviceId || !selectedTab || !isConnected) {
      return;
    }
    const requestId = ++schemaRequestIdRef.current;
    if (!silent) {
      setIsLoadingSchema(true);
    }
    try {
      const schema = await getDeviceConfigCategory({ deviceId, category: selectedTab });
      if (requestId !== schemaRequestIdRef.current) {
        // A newer tab switch started another request while this one was in flight — discard this stale result.
        return;
      }
      setFormSchema(schema);
    } catch (error: any) {
      if (requestId !== schemaRequestIdRef.current) {
        return;
      }
      addMessage({ message: error.message });
      setFormSchema(null);
    } finally {
      if (requestId === schemaRequestIdRef.current) {
        setIsLoadingSchema(false);
      }
    }
  }, [deviceId, selectedTab, isConnected, getDeviceConfigCategory]);

  useEffect(() => {
    fetchSchema();
  }, [fetchSchema]);

  // Scroll to the line matching scrollToField (its `lineName`, e.g. "L1")
  // after the schema finishes loading. The caller can't know this line's
  // array index ahead of time (that requires having fetched this same
  // schema already), so we resolve it here — this component always has
  // `formSchema.configData.lines` once its own fetch completes. RJSF renders
  // each array item as <fieldset id="root_lines_<index>">.
  const hasScrolledToFieldRef = useRef(false);

  useEffect(() => {
    hasScrolledToFieldRef.current = false;
  }, [scrollToField, selectedTab]);

  useEffect(() => {
    if (!scrollToField || isLoadingSchema || selectedTab === TERMINAL_TAB || hasScrolledToFieldRef.current) {
      return;
    }
    const attempt = () => {
      let el: HTMLElement | null = null;
      const lines: any[] = formSchema?.configData?.lines;
      if (Array.isArray(lines)) {
        const idx = lines.findIndex((l: any) => l?.lineName === scrollToField);
        if (idx !== -1) {
          el = document.getElementById(`root_lines_${idx}`);
        }
      }
      // Fallback: scan legend/label text for a match (e.g. a schema shaped
      // differently than the `lines` array, or a label that didn't resolve).
      if (!el) {
        const needle = scrollToField.toLowerCase();
        const candidates = Array.from(document.querySelectorAll<HTMLElement>("legend, label, [data-field-name]"));
        el = candidates.find((c) => c.textContent?.toLowerCase().includes(needle)) ?? null;
      }
      if (el) {
        hasScrolledToFieldRef.current = true;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
    // Give React / RJSF one tick to flush the DOM after isLoadingSchema flips.
    const id = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(id);
  }, [scrollToField, isLoadingSchema, selectedTab, formSchema]);

  useEffect(() => {
    if (!isConnected) {
      setCategories({});
      setSelectedTab("");
    }
  }, [isConnected]);

  const tabKeys = useMemo(() => [...Object.keys(categories), TERMINAL_TAB], [categories]);

  // @rjsf/validator-ajv8 already dedups ajv's per-oneOf/anyOf-branch rejection noise itself
  // (see its `filterDuplicateErrors`, run internally before `transformErrors` is ever
  // called) — e.g. it collapses N near-identical "should be equal to constant" errors from
  // N non-matching `dependencies` branches down to 1. What's left after that is still one
  // message *per ajv keyword*, so a field can end up with more than one *distinct* message
  // (e.g. both "is a required property" and "should match exactly one schema in oneOf" for
  // the same dependency block) — none of that is actionable to show together. Collapse to
  // at most one message per field: prefer the first non-oneOf/const/enum error, otherwise
  // fall back to a single representative oneOf/const/enum message. Also drop a parent-level
  // oneOf summary once a more specific error already surfaces on one of its own descendant
  // fields.
  const transformErrors = (errors: any[]) => {
    const byField = new Map<string, any[]>();
    errors.forEach((error) => {
      const key = error.property ?? "";
      const existing = byField.get(key) ?? [];
      existing.push(error);
      byField.set(key, existing);
    });

    const winners: any[] = [];
    byField.forEach((fieldErrors) => {
      const meaningful = fieldErrors.find((error) => !["oneOf", "const", "enum"].includes(error.name));
      winners.push(meaningful ?? fieldErrors[0]);
    });

    return winners.filter((error) => {
      if (!["oneOf", "const", "enum"].includes(error.name)) return true;
      const hasDescendantError = winners.some(
        (other) => other !== error && other.property !== error.property && other.property.startsWith(error.property)
      );
      return !hasDescendantError;
    });
  };

  const handleSubmit = async (data: any) => {
    if (isSubmitting || !canUpdateDeviceConfig) {
      return;
    }
    setIsSubmitting(true);
    // Update configData before the request even starts, not after it resolves: setting
    // isSubmitting re-renders this component, which re-renders <Form formData={...}>, and
    // RJSF's Form re-syncs its internal state from that prop on every render. If configData
    // still held the pre-submit values at that point, the form would visibly revert to them
    // for the duration of the request before "snapping forward" once it completes.
    setFormSchema((prev: any) => ({ ...prev, configData: data.formData }));
    try {
      await setDeviceConfigCategory({ deviceId, category: selectedTab, data: data.formData });
      await fetchSchema(true);
    } catch (error: any) {
      addMessage({ message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const wrapWithProgress = useCallback(
    (fn: (cmd: any, print: (msg: string) => void) => void) => (cmd: any, print: (msg: string) => void) => {
      setIsTerminalBusy(true);
      fn(cmd, (msg: string) => {
        setIsTerminalBusy(false);
        print(msg);
      });
    },
    []
  );

  const renderTab = (key: string) => {
    if (key === TERMINAL_TAB) {
      return (
        <div className={styles.terminalContainer}>
          <Terminal
          // @ts-ignore
            startState="maximised"
            color="#f05347"
            outputColor="white"
            prompt={terminalConfig.promptColor}
            backgroundColor="black"
            barColor="black"
            style={terminalConfig.style}
            commands={Object.fromEntries(
              Object.entries(terminalConfig.commands).map(([cmdKey, fn]) => [cmdKey, wrapWithProgress(fn as any)])
            )}
            commandPassThrough={wrapWithProgress(terminalConfig.commandPassThrough)}
            msg="DEVICE CONTROL"
            allowTabs={false}
            showActions={false}
          />
        </div>
      );
    }
    if (!formSchema?.configSchema) {
      return (
        <div className={styles.centered}>
          <Typography>{t`Failed to load configuration for this category.`}</Typography>
        </div>
      );
    }
    return (
      <Form
        key={`form-${selectedTab}`}
        schema={formSchema?.configSchema}
        uiSchema={formSchema?.uiSchema}
        formData={formSchema?.configData}
        validator={validator}
        transformErrors={transformErrors}
        showErrorList={false}
        disabled={!canUpdateDeviceConfig}
        onSubmit={handleSubmit}
      >
        <>
          {canUpdateDeviceConfig && (
            <Button className={styles.submitButton} type="submit" disabled={isSubmitting}>
              {t`Submit`}
            </Button>
          )}
        </>
      </Form>
    );
  };

  if (!isConnected) {
    return (
      <div className={styles.offlineContainer}>
        {isRetrying ? (
          <div className={styles.centered}>
            <Spinner size={32} />
          </div>
        ) : (
          <>
            <Typography variant="h6">{t`Device Offline`}</Typography>
            <Typography className={styles.offlineBody}>
              {t`Unable to communicate with the device. Please check that the device is powered on and connected to the network.`}
            </Typography>
            <Button onClick={handleRetry}>{t`Try again`}</Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.tabsBar}>
        {isLoadingCategories ? (
          <div className={styles.centered}>
            <Spinner size={32} />
          </div>
        ) : (
          <Tabs
            value={selectedTab}
            onChange={(value: any) => setSelectedTab(String(value))}
            tabs={tabKeys.map((key) => ({ value: key, label: categories[key] ?? key }))}
          />
        )}
        <LinearProgress className={styles.progressBar} style={{ visibility: (isSubmitting || isTerminalBusy) ? "visible" : "hidden" }} />
      </div>

      {!isLoadingCategories && selectedTab && (
        <div className={styles.tabPanel}>
          {isLoadingSchema && selectedTab !== TERMINAL_TAB ? (
            <div className={styles.centered}>
              <Spinner size={32} />
            </div>
          ) : (
            renderTab(selectedTab)
          )}
        </div>
      )}
    </div>
  );
};

export default DeviceConfiguration;
